import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { SharedService } from "./service";
import { Observable } from "rxjs";
import { AuthService } from "./auth.service";
import { getBrowserLang } from "../helpers";

export function URI(literals: TemplateStringsArray, ...values: (number | boolean | string | { name: string; items: any[] })[]) {
  let result = '';
  const length = Math.max(literals.length, values.length);
  for (let i = 0; i < length; i++) {
    result += literals[i];
    const value = values[i];
    if (value !== undefined) {
      if (typeof value === 'object') {
        result += value.items.map(v => value.name + '=' + encodeURIComponent(v)).join('&');
      } else {
        result += encodeURIComponent(values[i].toString());
      }
    }
  }
  return result;
}

export function serializeQuery(params: any, prefix?: string) {
  const query : any = Object.keys(params).map((key) => {
    const value = params[key];
    if (value == undefined) {
      return;
    }

    if (params.constructor === Array) {
      key = `${prefix}`;
    } else if (params.constructor === Object) {
      key = (prefix ? `${prefix}.${key}` : key);
    }

    if (typeof value === 'object') {
      return serializeQuery(value, key);
    } else {
      return `${key}=${encodeURIComponent(value)}`;
    }
  });

  return [].concat.apply([], query).filter((q: any) => q !== undefined).join('&');
}

@Injectable()
export class HttpService {


  constructor(private httpClient: HttpClient, private authService: AuthService) {

  }

  private getHeaders() {

    let acceptLanguage = 'en-US';

    const browserLang = getBrowserLang();
    if (browserLang != null) {
      acceptLanguage = browserLang + ', ' + acceptLanguage;
    }

    const authInfo = this.authService.authInfoBehavior.value;
    return authInfo
      ? new HttpHeaders({ 'Accept-Language': acceptLanguage, 'Authorization': 'Bearer ' + authInfo.access_token })
      : new HttpHeaders({ 'Accept-Language': acceptLanguage });
  }

  get<T>(url: string, params?: any): Observable<T> {

    const headers = this.getHeaders();

    const options = params
      ? { responseType: 'json' as 'json', headers, params: new HttpParams({ fromString: serializeQuery(params) }) }
      : { responseType: 'json' as 'json', headers };

    return this.httpClient.get<T>(url, options);
  }

  getBlob(url: string, params?: any): Observable<Blob> {

    const headers = this.getHeaders();

    const options = params
      ? { responseType: 'blob' as 'blob', headers, params: new HttpParams({ fromString: serializeQuery(params) }) }
      : { responseType: 'blob' as 'blob', headers };

    return this.httpClient.get(url, options);
  }

  post<T>(url: string, body: any, params?: any): Observable<T> {

    const headers = this.getHeaders();

    const options = params
      ? { responseType: 'json' as 'json', headers, params: new HttpParams({ fromString: serializeQuery(params) }) }
      : { responseType: 'json' as 'json', headers };

    return this.httpClient.post<T>(url, body, options);
  }

  put<T>(url: string, body: any, params?: any): Observable<T> {

    const headers = this.getHeaders();

    const options = params
      ? { responseType: 'json' as 'json', headers, params: new HttpParams({ fromString: serializeQuery(params) }) }
      : { responseType: 'json' as 'json', headers };

    return this.httpClient.put<T>(url, body, options);
  }

  delete<T>(url: string, params?: any): Observable<T> {

    const headers = this.getHeaders();

    const options = params
      ? { responseType: 'json' as 'json', headers, params: new HttpParams({ fromString: serializeQuery(params) }) }
      : { responseType: 'json' as 'json', headers };

    return this.httpClient.delete<T>(url, options);
  }
}
